import { CircularProgress, styled } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useState } from "react";
import { ReactComponent as RoleIcon } from '../../../../svg/adminRoleIcon.svg';
import { ReactComponent as Arrow } from '../../../../svg/arrow.svg';
import apiCall from "../../../../utils/apiCall";
import SettingsModal from '../Modal/settingsCustomModal';
import DisableRoleContent from "./DisableRole";
import EditRoleContent from "./EditRole";
import ViewContent from "./ViewContent";
import ViewRoleContent from "./ViewRoleContent";

interface AdminRole {
    Id: string;
    Name: string;
    NormalizedName: string;
}

interface AdminRoleProps {
    data: AdminRole[];
}

const AdminRoleContent = () => {
    const [openRoleModal, setOpenRoleModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [openDisableModal, setOpenDisableModal] = useState(false);
    const [roleName, setRoleName] = useState('');
    const [roleId, setRoleId] = useState('');

    const handleOpenRoleModal = () => setOpenRoleModal(true);
    const handleCloseRoleModal = () => setOpenRoleModal(false);

    const handleOpenEditModal = () => setOpenEditModal(true);
    const handleCloseEditModal = () => setOpenEditModal(false);

    const handleOpenDisableModal = () => setOpenDisableModal(true);
    const handleCloseDisableModal = () => setOpenDisableModal(false);

    const handleDisable = () => {
        handleCloseRoleModal();
        handleOpenDisableModal();
    };
    
    const handleEdit = () => {
        handleCloseRoleModal();
        handleOpenEditModal();
    };

    const handleOpen = (RoleData: AdminRole) => {
        setRoleName(RoleData?.Name);
        setRoleId(RoleData?.Id)
        handleOpenRoleModal();
    }

    const getAllRole = async (): Promise<AdminRoleProps> => {
        const url = '/Administration/roles/readall';
        return (await apiCall().get(url)); 
    };
    
    const { data, isLoading, isError, error } = useQuery(['get-all-role'], getAllRole, {
        retry: 0,
        enabled: true,
        keepPreviousData: true,
    });

    const adminRoleError = error as AxiosError;
    const adminRoleErrorMessage = adminRoleError?.message;

    return (
        <>
            {isLoading ? (
                <CircularProgress 
                sx={{
                    fontSize: '1rem',
                    marginInline: 'auto',
                    display: 'block',
                    marginBlock: '2rem'
                }}
                />
            ) : isError ? (
                <StyledError>{adminRoleErrorMessage}</StyledError>
            ) : (
                <StyledContainer>
                    <TotalWrapper>
                        <span>Total</span>
                        <TotalNumberStyle>{data?.data?.length}</TotalNumberStyle>
                    </TotalWrapper>
                    {data?.data && data?.data?.length > 0 &&
                        data?.data?.map((data, index) => (
                        <RoleWrapper key={index} onClick={() => handleOpen(data)}>
                            <div>
                                <RoleIconWrapper><RoleIcon /></RoleIconWrapper>
                                <RoleText>{data?.Name}</RoleText>
                                <Active>{ 'Active'}</Active>
                                <DateWrapper></DateWrapper>
                            </div>
                            <ArrowIconWrapper>
                                <Arrow />
                            </ArrowIconWrapper>
                        </RoleWrapper>
                    ))}

                    <SettingsModal 
                        open={openRoleModal}
                        cancel={handleCloseRoleModal}
                        maxWidth='712px'
                        header={roleName}
                        content={<ViewRoleContent name={roleName} />}
                        showrightIcon={false}
                        card={<ViewContent handleEdit={handleEdit} handleDisable={handleDisable}/>}
                    />

                    <SettingsModal
                        open={openEditModal}
                        cancel={handleCloseEditModal}
                        maxWidth='480px'
                        header='Edit Role'
                        showrightIcon={false}
                        content={<EditRoleContent />}   
                    />

                    <SettingsModal
                        open={openDisableModal}
                        cancel={handleCloseDisableModal}
                        maxWidth='653px'
                        header='Delete Role'
                        showrightIcon={false}
                        content={<DisableRoleContent />}   
                    />
                </StyledContainer>
            )} 
        </> 
    )
}

export default AdminRoleContent;

const StyledContainer = styled('div')`
    margin-block: 3.8rem;
    margin-inline: 3.5rem;
`

const TotalNumberStyle = styled('span')`
    background-color: #F1F3F4;
    border-radius: 0.2rem;
    margin-inline-start: 0.4rem;
    padding-inline: 0.2rem;
`

const TotalWrapper = styled('div')`
    color: #4E5A62;
    font-weight: 500;
`

const RoleWrapper = styled('div')`
    display: flex;
    justify-content: space-between;
    border: 0.1rem solid #E1E1E1;
    margin-block: 0.6rem;
    padding-inline: 1.2rem;
    padding-block: 1.2rem;
    border-radius: 0.5rem;
    width: 100%;
    text-align: left;
    background-color: transparent;
    cursor: pointer;
`

const RoleIconWrapper = styled('span')`
    background-color: #FFF4DE;
    border-radius: 0.1rem;
    padding-inline: 0.4rem;
`

const RoleText = styled('span')`
    font-weight: 600;
    font-size: 16px;
    margin-inline: 0.8rem;
`

const Active = styled('span')`
    font-size: 14px;
    font-weight: 500;
    background-color: #E0FAEE;
    border-radius: 0.4rem;
    padding-inline: 0.5rem;
    color: #11643C;
`

const DateWrapper = styled('div')`
    margin-block-start: 0.8rem;
`

const ArrowIconWrapper = styled('div')`
    display: flex;
    flex-direction: column;
    justify-content: center;
`

const StyledError = styled('p')`
  color: red;
  font-size: 1.4rem;
  display: flex;
`